import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AlertCircle, ArrowRight } from "lucide-react";

/**
 * PendingYouBanner — shows a compact card on top of the Procurement page listing
 * exactly what actions are waiting on the current user's role.
 *
 *   Admin sees:  POs pending approval · GRNs pending approval
 *   Staff sees:  My draft POs · Rejected POs to fix
 *
 * Clicking a bucket switches the parent tab + sets a status filter.
 */
export default function PendingYouBanner({ onJumpTo }) {
  const { user } = useAuth();
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [posRes, grnsRes] = await Promise.all([
        api.get("/staff/purchase-orders"),
        api.get("/staff/grns"),
      ]);
      const pos = posRes.data || [];
      const grns = grnsRes.data || [];
      const isAdmin = user?.role === "admin";
      const email = user?.email || "";
      const list = [];
      if (isAdmin) {
        const poPending = pos.filter((p) => p.status === "pending_approval");
        const grnPending = grns.filter((g) => g.status === "pending_approval");
        if (poPending.length) list.push({ key: "po-pending", tab: "pos", filter: "pending_approval", label: "POs pending your approval", count: poPending.length, tone: "danger" });
        if (grnPending.length) list.push({ key: "grn-pending", tab: "grns", filter: "pending_approval", label: "GRNs pending your approval", count: grnPending.length, tone: "danger" });
      } else {
        const myDrafts = pos.filter((p) => p.status === "draft" && (p.created_by === email || !p.created_by));
        const rejected = pos.filter((p) => p.status === "rejected" && (p.created_by === email || !p.created_by));
        if (myDrafts.length) list.push({ key: "my-drafts", tab: "pos", filter: "draft", label: "Your draft POs (submit to admin)", count: myDrafts.length, tone: "warn" });
        if (rejected.length) list.push({ key: "my-rejected", tab: "pos", filter: "rejected", label: "Rejected POs — fix & resubmit", count: rejected.length, tone: "danger" });
      }
      setBuckets(list);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.role, user?.email]);

  if (loading || buckets.length === 0) return null;

  return (
    <section data-testid="pending-you-banner" className="mt-4 border border-[hsl(var(--brass))] bg-[hsl(var(--brass))]/8 p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="text-[hsl(var(--brass-2))] mt-0.5 shrink-0" size={18} />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] font-semibold">Pending you</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {buckets.map((b) => (
              <button
                key={b.key}
                data-testid={`pending-you-${b.key}`}
                onClick={() => onJumpTo?.(b.tab, b.filter)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  b.tone === "danger"
                    ? "border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)] hover:bg-[hsl(6_62%_45%)] hover:text-white"
                    : "border-[hsl(var(--brass-2))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass-2))] hover:text-white"
                }`}
              >
                <span className="font-semibold">{b.count}</span>
                <span>{b.label}</span>
                <ArrowRight size={12} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
